<script>
import { mapGetters, mapActions } from "vuex";
import axios from "axios";
import CtSelect from "@/components/CtSelect.vue";

export default {
  components: { CtSelect },
  data: () => ({
    canCreate: false,
    canUpdate: false,
    canDelete: false,
    isLoading: false,
    options: { page: 1, itemsPerPage: 20 },
    totalItemsCount: 0,
    search: "",
    searchTimeout: null,

    // Right-hand fast filters: "" = Barchasi. filterKurs is a course number as a string, or
    // "default" for the rows with no kurs (shown as "Barcha kurslar").
    filterYear: "",
    filterKurs: "",
    // The yellow pill behind each block's selected button: one element per block that slides to the
    // clicked button, like an iOS segmented control. Inline styles set by placeThumb(); hidden until
    // first placed, and it only starts sliding once it has landed (thumbsReady).
    thumbs: { year: { opacity: 0 }, kurs: { opacity: 0 } },
    thumbsReady: false,

    // Upload modal
    showUpload: false,
    uploadForm: { code: "", name: "", kurs: "", year: "2026-2027" },
    uploadFile: null,
    yearOptions: ["2022-2023", "2023-2024", "2024-2025", "2025-2026", "2026-2027"],
    isDragging: false,
    isUploading: false,

    // Edit modal
    showEdit: false,
    editItem: null,
    editForm: { name: "" },
    editFile: null,
    isEditDragging: false,
    isEditing: false,

    // Delete confirm modal
    showDelete: false,
    deleteId: null,
    deleteCode: "",
    deleteName: "",
    isDeleting: false,
  }),

  computed: {
    ...mapGetters("admin-contract-templates", ["GET_TEMPLATES"]),
    items: (vm) => vm.GET_TEMPLATES,
    kursOptions() {
      return [
        { value: "", label: this.$t("All courses") },
        ...[1, 2, 3, 4].map((n) => ({ value: String(n), label: `${n}-${this.$t("kurs")}` })),
      ];
    },
    yearSelectOptions() {
      return this.yearOptions.map((year) => ({ value: year, label: year }));
    },
    // Newest year first, as in the Davrlar dialog.
    yearFilterOptions() {
      return [...this.yearOptions].reverse();
    },
  },

  watch: {
    search() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.options.page = 1;
        this.getItems();
      }, 400);
    },
    filterYear() {
      this.$nextTick(() => this.placeThumb("year"));
    },
    filterKurs() {
      this.$nextTick(() => this.placeThumb("kurs"));
    },
  },

  async mounted() {
    this.$nextTick(() => {
      this.placeThumbs();
      this.$nextTick(() => {
        void this.$refs.yearSeg.offsetWidth; // commit the first position before sliding is switched on
        this.thumbsReady = true;
      });
    });
    // Wrapping above the table, the menu or the window can move the buttons: keep each pill under its own.
    if (typeof ResizeObserver === "function") {
      this.thumbObserver = new ResizeObserver(() => this.placeThumbs());
      this.thumbObserver.observe(this.$refs.yearSeg);
      this.thumbObserver.observe(this.$refs.kursSeg);
    }

    this.canCreate = await this.hasRole("SHARTNOMA_SHABLONLARI", "CREATE");
    this.canUpdate = await this.hasRole("SHARTNOMA_SHABLONLARI", "UPDATE");
    this.canDelete = await this.hasRole("SHARTNOMA_SHABLONLARI", "DELETE");
  },

  created() {
    this.getItems();
  },

  beforeDestroy() {
    if (this.thumbObserver) this.thumbObserver.disconnect();
  },

  methods: {
    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },

    ...mapActions("admin-contract-templates", [
      "INIT_TEMPLATES",
      "UPLOAD_TEMPLATE",
      "UPDATE_TEMPLATE",
      "DELETE_TEMPLATE",
    ]),

    getItems() {
      this.isLoading = true;
      this.INIT_TEMPLATES({
        ...this.options,
        search: this.search,
        studyYear: this.filterYear,
        kurs: this.filterKurs,
      }).then((total) => {
        this.totalItemsCount = total;
        this.isLoading = false;
      });
    },

    selectYear(y) {
      this.filterYear = y;
      this.options.page = 1;
      this.getItems();
    },

    selectKurs(k) {
      this.filterKurs = k;
      this.options.page = 1;
      this.getItems();
    },

    // Moves a block's pill ("year" or "kurs") onto its selected button. offsetLeft/offsetTop are
    // measured from .seg, which is the pill's positioning box, and ignore the press scale.
    placeThumb(which) {
      const seg = this.$refs[`${which}Seg`];
      const btn = seg && seg.querySelector(".seg__btn--active");
      if (!btn) return;
      this.thumbs[which] = {
        width: `${btn.offsetWidth}px`,
        height: `${btn.offsetHeight}px`,
        transform: `translate(${btn.offsetLeft}px, ${btn.offsetTop}px)`,
      };
    },

    placeThumbs() {
      this.placeThumb("year");
      this.placeThumb("kurs");
    },

    // ── Upload ──
    openUpload() {
      this.uploadForm = { code: "", name: "", kurs: "", year: "2026-2027" };
      this.uploadFile = null;
      this.showUpload = true;
    },

    onFileSelect(e) {
      const f = e.target.files[0];
      if (f && f.name.endsWith(".docx")) this.uploadFile = f;
    },

    onDragOver(e) {
      e.preventDefault();
      this.isDragging = true;
    },

    onDragLeave() {
      this.isDragging = false;
    },

    onDrop(e) {
      e.preventDefault();
      this.isDragging = false;
      const f = e.dataTransfer.files[0];
      if (f && f.name.endsWith(".docx")) this.uploadFile = f;
    },

    async submitUpload() {
      if (!this.uploadFile || !this.uploadForm.code || !this.uploadForm.name || !this.uploadForm.year) return;
      this.isUploading = true;
      const fd = new FormData();
      fd.append("file", this.uploadFile);
      fd.append("code", this.uploadForm.code);
      fd.append("name", this.uploadForm.name);
      // empty kurs => default template for all courses (backend stores NULL)
      if (this.uploadForm.kurs !== "" && this.uploadForm.kurs !== null) {
        fd.append("kurs", this.uploadForm.kurs);
      }
      fd.append("study_year", this.uploadForm.year);
      const res = await this.UPLOAD_TEMPLATE(fd);
      this.isUploading = false;
      if (res) {
        this.showUpload = false;
        this.getItems();
        this.$store.dispatch("SET_SNACKBAR", { text: this.$t("Success"), color: "success" }, { root: true });
      }
    },

    // ── Edit ──
    openEdit(item) {
      this.editItem = item;
      this.editForm = { name: item.name };
      this.editFile = null;
      this.isEditDragging = false;
      this.showEdit = true;
    },

    onEditFileSelect(e) {
      const f = e.target.files[0];
      if (f && f.name.endsWith(".docx")) this.editFile = f;
    },

    onEditDragOver(e) {
      e.preventDefault();
      this.isEditDragging = true;
    },

    onEditDragLeave() {
      this.isEditDragging = false;
    },

    onEditDrop(e) {
      e.preventDefault();
      this.isEditDragging = false;
      const f = e.dataTransfer.files[0];
      if (f && f.name.endsWith(".docx")) this.editFile = f;
    },

    async submitEdit() {
      if (!this.editForm.name) return;
      this.isEditing = true;
      const fd = new FormData();
      fd.append("name", this.editForm.name);
      if (this.editFile) fd.append("file", this.editFile);
      const res = await this.UPDATE_TEMPLATE({ id: this.editItem.id, formData: fd });
      this.isEditing = false;
      if (res) {
        this.showEdit = false;
        this.getItems();
        this.$store.dispatch("SET_SNACKBAR", { text: this.$t("Success"), color: "success" }, { root: true });
      }
    },

    // ── Delete ──
    openDelete(item) {
      this.deleteId = item.id;
      this.deleteCode = item.code;
      this.deleteName = item.name;
      this.showDelete = true;
    },

    async confirmDelete() {
      this.isDeleting = true;
      const res = await this.DELETE_TEMPLATE(this.deleteId);
      this.isDeleting = false;
      if (res !== undefined) {
        this.showDelete = false;
        this.getItems();
        this.$store.dispatch("SET_SNACKBAR", { text: this.$t("Success"), color: "success" }, { root: true });
      }
    },

    kursLabel(kurs) {
      return (kurs === null || kurs === undefined || kurs === "")
        ? this.$t("All courses")
        : `${kurs}-${this.$t("kurs")}`;
    },

    async downloadTemplate(item) {
      try {
        const res = await axios.get(`admin/templates/id/${item.id}/download`, { responseType: "blob" });
        const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = item.fileName || `${item.code}.docx`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch {
        this.$store.dispatch("SET_SNACKBAR", { text: this.$t("Error"), color: "error" }, { root: true });
      }
    },

    fmtDate(d) {
      if (!d) return "—";
      return new Date(d).toLocaleDateString("uz-UZ", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    },

    parsePlaceholders(json) {
      try {
        const arr = JSON.parse(json);
        return arr.join(", ");
      } catch {
        return "";
      }
    },

    totalPages() {
      return Math.ceil(this.totalItemsCount / this.options.itemsPerPage) || 1;
    },

    prevPage() {
      if (this.options.page > 1) {
        this.options.page--;
        this.getItems();
      }
    },
    nextPage() {
      if (this.options.page < this.totalPages()) {
        this.options.page++;
        this.getItems();
      }
    },
  },
};
</script>

<template>
  <div class="ct">
    <!-- Header -->
    <div class="ct__header">
      <h2 class="ct__title">{{ $t("Shartnoma shablonlari") }}</h2>
      <div class="ct__actions">
        <v-text-field
          :placeholder="$t('Izlash...')"
          append-icon="mdi-magnify"
          v-model="search"
          dense
          hide-details
          outlined
          clearable
          class="search-input"
        />
        <button v-if="canCreate" class="ct__btn ct__btn--primary" @click="openUpload">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          {{ $t("Upload") }}
        </button>
      </div>
    </div>

    <div class="ct__body">
      <!-- Fast filters, the same sidebar as the Davrlar dialog (PeriodsPricesDialog). First in the
           markup on purpose: .ct__body runs right to left, so it shows on the right, and when it
           wraps for lack of width it lands above the table. -->
      <aside class="sidebar">
        <!-- Academic year -->
        <div class="sb-section">
          <div class="sb-title">{{ $t('Academic year') }}</div>
          <div class="seg seg--col" ref="yearSeg">
            <span class="seg__thumb" :class="{ 'seg__thumb--ready': thumbsReady }" :style="thumbs.year" aria-hidden="true"></span>
            <button
                v-for="y in yearFilterOptions"
                :key="y"
                class="seg__btn seg__btn--col"
                :class="{ 'seg__btn--active': filterYear === y }"
                @click="selectYear(y)"
            >
              {{ y }}
            </button>
            <button
                class="seg__btn seg__btn--col seg__btn--ghost"
                :class="{ 'seg__btn--active': filterYear === '' }"
                @click="selectYear('')"
            >{{ $t('All') }}</button>
          </div>
        </div>

        <!-- Course -->
        <div class="sb-section">
          <div class="sb-title">{{ $t('Course') }}</div>
          <div class="seg seg--col" ref="kursSeg">
            <span class="seg__thumb" :class="{ 'seg__thumb--ready': thumbsReady }" :style="thumbs.kurs" aria-hidden="true"></span>
            <button
                v-for="c in ['1','2','3','4']"
                :key="'c' + c"
                class="seg__btn seg__btn--col"
                :class="{ 'seg__btn--active': filterKurs === c }"
                @click="selectKurs(c)"
            >{{ c }}</button>
            <button
                class="seg__btn seg__btn--col"
                :class="{ 'seg__btn--active': filterKurs === 'default' }"
                @click="selectKurs('default')"
            >{{ $t('All courses') }}</button>
            <button
                class="seg__btn seg__btn--col seg__btn--ghost"
                :class="{ 'seg__btn--active': filterKurs === '' }"
                @click="selectKurs('')"
            >{{ $t('All') }}</button>
          </div>
        </div>
      </aside>

      <div class="ct__main">
        <!-- Table -->
        <div class="ct__table-wrap">
          <table class="ct__table">
            <thead>
              <tr>
                <th>#</th>
                <th>{{ $t("Code") }}</th>
                <th>{{ $t("Name") }}</th>
                <th>{{ $t("Course") }}</th>
                <th>{{ $t("Academic year") }}</th>
                <th>{{ $t("Placeholders") }}</th>
                <th>{{ $t("Date") }}</th>
                <th>{{ $t("Actions") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="isLoading">
                <td colspan="8" class="ct__empty">{{ $t("Loading") }}...</td>
              </tr>
              <tr v-else-if="!items.length">
                <td colspan="8" class="ct__empty">{{ $t("No data") }}</td>
              </tr>
              <tr v-for="(item, i) in items" :key="item.id" v-else>
                <td>{{ (options.page - 1) * options.itemsPerPage + i + 1 }}</td>
                <td><span class="ct__code">{{ item.code }}</span></td>
                <td>{{ item.name }}</td>
                <td>{{ kursLabel(item.kurs) }}</td>
                <td>{{ item.studyYear || "—" }}</td>
                <td><span class="ct__placeholders">{{ parsePlaceholders(item.placeholders) }}</span></td>
                <td>{{ fmtDate(item.createdAt) }}</td>
                <td>
                  <div class="ct__row-actions">
                    <button class="tc-act tc-act--download" @click="downloadTemplate(item)" :title="$t('Yuklab olish')">
                      <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
                    </button>
                    <button v-if="canUpdate" class="tc-act" @click="openEdit(item)" :title="$t('Tahrirlash')">
                      <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
                    </button>
                    <button v-if="canDelete" class="tc-act tc-act--delete" @click="openDelete(item)" :title="$t('Delete')">
                      <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="ct__pagination" v-if="totalItemsCount > options.itemsPerPage">
          <button class="ct__page-btn" :disabled="options.page <= 1" @click="prevPage">&laquo;</button>
          <span class="ct__page-info">{{ options.page }} / {{ totalPages() }}</span>
          <button class="ct__page-btn" :disabled="options.page >= totalPages()" @click="nextPage">&raquo;</button>
        </div>
      </div>
    </div>

    <!-- Upload Modal -->
    <transition name="ct-fade">
      <div class="ct__overlay" v-if="showUpload" @click.self="showUpload = false">
        <transition name="ct-slide">
          <div class="ct__modal" v-if="showUpload">
            <div class="ct__modal-header">
              <h3>{{ $t("Upload") }} .docx</h3>
              <button class="ct__modal-x" @click="showUpload = false">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div class="ct__modal-body">
              <div class="ct__field">
                <label>{{ $t("Code") }}</label>
                <input v-model="uploadForm.code" placeholder="11000" />
              </div>
              <div class="ct__field">
                <label>{{ $t("Name") }}</label>
                <input v-model="uploadForm.name" :placeholder="$t('Template name')" />
              </div>
              <div class="ct__field">
                <label>{{ $t("Course") }}</label>
                <CtSelect v-model="uploadForm.kurs" :options="kursOptions" />
              </div>
              <div class="ct__field">
                <label>{{ $t("Academic year") }}</label>
                <CtSelect v-model="uploadForm.year" :options="yearSelectOptions" />
              </div>
              <div
                class="ct__dropzone"
                :class="{ 'ct__dropzone--active': isDragging, 'ct__dropzone--has-file': uploadFile }"
                @dragover="onDragOver"
                @dragleave="onDragLeave"
                @drop="onDrop"
                @click="$refs.fileInput.click()"
              >
                <input ref="fileInput" type="file" accept=".docx" hidden @change="onFileSelect" />
                <template v-if="uploadFile">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <p>{{ uploadFile.name }}</p>
                </template>
                <template v-else>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <p>{{ $t("Drag & drop .docx or click") }}</p>
                </template>
              </div>
            </div>
            <div class="ct__modal-footer">
              <button class="ct__btn" @click="showUpload = false">{{ $t("Cancel") }}</button>
              <button class="ct__btn ct__btn--primary" @click="submitUpload" :disabled="isUploading || !uploadFile || !uploadForm.code || !uploadForm.name || !uploadForm.year">
                {{ isUploading ? $t("Loading") + '...' : $t("Upload") }}
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>

    <!-- Edit Modal -->
    <transition name="ct-fade">
      <div class="ct__overlay" v-if="showEdit" @click.self="showEdit = false">
        <transition name="ct-slide">
          <div class="ct__modal" v-if="showEdit">
            <div class="ct__modal-header">
              <h3>{{ $t("Tahrirlash") }} — {{ editItem && editItem.code }}</h3>
              <button class="ct__modal-x" @click="showEdit = false">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div class="ct__modal-body">
              <div class="ct__field">
                <label>{{ $t("Name") }}</label>
                <input v-model="editForm.name" :placeholder="$t('Template name')" />
              </div>
              <div
                class="ct__dropzone"
                :class="{ 'ct__dropzone--active': isEditDragging, 'ct__dropzone--has-file': editFile }"
                @dragover="onEditDragOver"
                @dragleave="onEditDragLeave"
                @drop="onEditDrop"
                @click="$refs.editFileInput.click()"
              >
                <input ref="editFileInput" type="file" accept=".docx" hidden @change="onEditFileSelect" />
                <template v-if="editFile">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  <p>{{ editFile.name }}</p>
                </template>
                <template v-else>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <p>{{ $t("Drag & drop .docx or click") }}</p>
                  <p class="ct__hint" style="margin:0">{{ editItem && editItem.fileName }}</p>
                </template>
              </div>
            </div>
            <div class="ct__modal-footer">
              <button class="ct__btn" @click="showEdit = false">{{ $t("Cancel") }}</button>
              <button class="ct__btn ct__btn--primary" @click="submitEdit" :disabled="isEditing || !editForm.name">
                {{ isEditing ? $t("Loading") + '...' : $t("Save") }}
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>

    <!-- Delete Confirm Modal -->
    <transition name="ct-fade">
      <div class="ct__overlay" v-if="showDelete" @click.self="showDelete = false">
        <transition name="ct-slide">
          <div class="ct__modal ct__modal--sm" v-if="showDelete">
            <div class="ct__modal-header">
              <h3>{{ $t("Delete") }}</h3>
              <button class="ct__modal-x" @click="showDelete = false">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div class="ct__modal-body">
              <p>{{ $t("Are you sure?") }}</p>
              <p class="ct__delete-name"><b>{{ deleteCode }}</b> — {{ deleteName }}</p>
            </div>
            <div class="ct__modal-footer">
              <button class="ct__btn" @click="showDelete = false">{{ $t("Cancel") }}</button>
              <button class="ct__btn ct__btn--danger" @click="confirmDelete" :disabled="isDeleting">
                {{ isDeleting ? $t("Loading") + '...' : $t("Delete") }}
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
/* ── Base ── */
.ct { padding: 0 12px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

/* ── Header ── */
.ct__header { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
.ct__title { font-size: 20px; font-weight: 700; color: var(--v-text-base, #1a1a2e); margin: 0; }
.ct__actions { display: flex; align-items: center; gap: 10px; }
::v-deep .search-input {
  .v-input__slot {
    background: #f1f5f9 !important;
    border-radius: 10px !important;
    min-height: 38px !important;
  }
  fieldset { border-color: transparent !important; }
  &:hover fieldset { border-color: #d0d5dd !important; }
  &.v-input--is-focused fieldset { border-color: #00274c !important; border-width: 1.5px !important; }
  .v-icon { color: #94a3b8 !important; font-size: 20px !important; }
  input { font-size: 14px; }
  input::placeholder { color: #94a3b8 !important; }
}
::v-deep .search-input.theme--dark {
  .v-input__slot { background: #22232e !important; }
  fieldset { border-color: transparent !important; }
  &:hover fieldset { border-color: #33354a !important; }
  &.v-input--is-focused fieldset { border-color: #ffd451 !important; }
  .v-icon { color: #606275 !important; }
  input { color: #d5d6de !important; }
  input::placeholder { color: #606275 !important; }
}

/* ── Buttons ── */
.ct__btn {
  padding: 0 18px; height: 38px; border: 1px solid #e0e3eb; border-radius: 10px; background: #fff;
  font-size: 13px; font-weight: 600; cursor: pointer; transition: all .2s ease;
  display: inline-flex; align-items: center; gap: 6px; color: #374151;
}
.ct__btn:hover { background: #f3f4f6; border-color: #d1d5db; transform: translateY(-1px); }
.ct__btn:disabled { opacity: .5; cursor: not-allowed; transform: none; }

.ct__btn--primary {
  background: #ffd451; color: #1a1a2e; border-color: #ffd451;
  box-shadow: 0 2px 8px rgba(255,212,81,.25);
}
.ct__btn--primary:hover {
  background: #ffcc33; border-color: #ffcc33;
  box-shadow: 0 4px 14px rgba(255,212,81,.35);
  transform: translateY(-1px);
}
.ct__btn--primary:active {
  background: #e6be3f;
  box-shadow: 0 1px 4px rgba(255,212,81,.2);
}

.ct__btn--danger {
  background: #dc2626; color: #fff; border-color: #dc2626;
  box-shadow: 0 2px 8px rgba(220,38,38,.2);
}
.ct__btn--danger:hover {
  background: #c52222; border-color: #c52222;
  box-shadow: 0 4px 14px rgba(220,38,38,.3);
  transform: translateY(-1px);
}
.ct__btn--danger:active {
  background: #b91c1c;
  box-shadow: 0 1px 4px rgba(220,38,38,.15);
}

/* Press feedback for every button on the page (Tailwind's active:scale-[0.98] duration-150): each
   one shrinks slightly while held. .ct also wraps the modals, so their buttons are included. */
.ct button { transition-duration: .15s; }
.ct button:active:not(:disabled) { transform: scale(.98); }

/* ── Table ── */
/* Scrolls sideways when the page is narrower than the table, instead of cutting off its last
   columns (the action buttons) */
.ct__table-wrap { background: #fff; border: 1px solid #e0e3eb; border-radius: 12px; overflow-x: auto; }
.ct__table { width: 100%; border-collapse: collapse; font-size: 13px; }
.ct__table thead { background: #f8f9fd; }
.ct__table th { padding: 12px 16px; text-align: left; font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e0e3eb; white-space: nowrap; }
.ct__table td { padding: 12px 16px; border-bottom: 1px solid #f3f4f6; color: #374151; vertical-align: middle; }
.ct__table tbody tr { transition: background .15s; }
.ct__table tbody tr:hover { background: #f8f9fd; }
.ct__table tbody tr:last-child td { border-bottom: none; }
.ct__code { background: #fff8e1; color: #8a6d00; padding: 2px 8px; border-radius: 4px; font-weight: 700; font-size: 12px; font-family: monospace; }
.ct__placeholders { font-size: 12px; color: #9ca3af; max-width: 200px; display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ct__row-actions { display: flex; gap: 6px; justify-content: flex-end; }
.tc-act {
  display: flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e5e7eb;
  background: none; color: #475569; cursor: pointer; transition: all .2s ease;
}
.tc-act:hover { border-color: #ffd451; color: #ffd451; background: rgba(255,212,81,.06); transform: scale(1.08); }
.tc-act--download:hover { border-color: #3b82f6; color: #3b82f6; background: rgba(59,130,246,.06); }
.tc-act--delete:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,.06); }
.ct__empty { text-align: center; padding: 40px; color: #9ca3af; }

/* ── Pagination ── */
.ct__pagination { display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 16px; }
.ct__page-btn {
  padding: 6px 14px; border: 1px solid #e0e3eb; border-radius: 6px; background: #fff;
  cursor: pointer; font-size: 14px; font-weight: 600; transition: all .2s ease;
}
.ct__page-btn:disabled { opacity: .4; cursor: not-allowed; }
.ct__page-btn:hover:not(:disabled) { background: #f3f4f6; transform: translateY(-1px); }
.ct__page-info { font-size: 13px; color: #6b7280; font-weight: 500; }

/* ── Layout: table on the left, fast filters on the right ──
   Sized by the width the page really has (the menu may be open or closed), not by the screen: the
   filters stay on the right while the table keeps 900px, and wrap above it, full width, once it
   would not. The aside is first in the markup and the row runs right to left, so it shows on the
   right and a wrapped aside lands on top. */
.ct__body { display:flex; flex-direction:row-reverse; flex-wrap:wrap; gap:16px; align-items:flex-start; }
.ct__main { flex: 999 1 900px; min-width: 0; }

/* Sidebar, copied from the Davrlar dialog (PeriodsPricesDialog.vue) so both panels look the same.
   Its blocks and buttons wrap instead of always stacking: at the sidebar's 232px that is still a
   single column, as in the dialog, and above the table they spread out into rows. */
.sidebar { flex: 1 1 232px; display:flex; flex-wrap:wrap; gap:12px; }
.sb-section { flex: 1 1 220px; background: rgba(255,255,255,.85); border:1px solid rgba(0,30,60,.08); border-radius:12px; padding:10px; }
.sb-title { font-weight:700; font-size:12.5px; color:#334155; margin: 0 0 8px; }

.seg {
  position:relative;
  display:flex; align-items:center; gap:6px;
  background:#eef2f7;
  border:1px solid rgba(0,30,60,.08);
  border-radius:999px;
  padding:6px;
  box-shadow:0 4px 12px rgba(2,6,23,.05);
  flex-wrap: wrap;
}
.seg--col { flex-direction:row; align-items:stretch; }
.seg__btn{
  position:relative; z-index:1;
  appearance:none; border:0; background:transparent; color:#475569;
  font-weight:700; letter-spacing:.3px; font-size:13.5px; padding:8px 14px;
  border-radius:999px; cursor:pointer; min-width: 100px; text-align:center;
  transition: transform .12s ease, background-color .15s ease, box-shadow .15s ease, color .15s ease, border-color .15s ease;
  -webkit-tap-highlight-color: transparent; user-select:none;
}
/* One per row in the 232px sidebar (min-width 100px leaves no room for a second), two or three per
   row once the block is wider. */
.seg__btn--col { flex: 1 1 30%; }
.seg__btn--ghost { opacity:.9; }
.seg__btn--active{ color:#111827; }

/* The selected button's yellow pill, painted by one element per block that slides from the old
   choice to the new one, like an iOS segmented control; the buttons sit above it. */
.seg__thumb {
  position:absolute; top:0; left:0; z-index:0; pointer-events:none; box-sizing:border-box;
  background:#fde047;
  box-shadow:0 6px 18px rgba(245,158,11,.22);
  border:1px solid rgba(0,30,60,.12);
  border-radius:999px;
}
.seg__thumb--ready {
  transition: transform .3s cubic-bezier(.25,.8,.25,1), width .3s cubic-bezier(.25,.8,.25,1),
              height .3s cubic-bezier(.25,.8,.25,1);
}
@media (prefers-reduced-motion: reduce) {
  .seg__thumb--ready { transition: none; }
}

/* ── Modal ── */
.ct__overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 1000;
  display: flex; align-items: center; justify-content: center; padding: 20px;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}
.ct__modal {
  background: #fff; border-radius: 16px; width: 100%; max-width: 480px;
  box-shadow: 0 24px 64px rgba(0,0,0,.18);
  max-height: 90vh; display: flex; flex-direction: column;
}
.ct__modal--sm { max-width: 400px; }
.ct__modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px; border-bottom: 1px solid #f3f4f6; }
.ct__modal-header h3 { font-size: 16px; font-weight: 700; margin: 0; color: #1a1a2e; }

/* ── Modal X close button ── */
.ct__modal-x {
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border-radius: 8px; border: none;
  background: #f3f4f6; color: #6b7280; cursor: pointer;
  transition: all .2s ease;
}
.ct__modal-x:hover { background: #fee2e2; color: #dc2626; }

.ct__modal-body { padding: 20px 24px; overflow-y: auto; }
.ct__modal-footer { display: flex; justify-content: flex-end; align-items: stretch; gap: 10px; padding: 16px 24px; border-top: 1px solid #f3f4f6; }
.ct__modal-footer .ct__btn { padding: 10px 20px; }

/* ── Form fields ── */
.ct__field { margin-bottom: 14px; }
.ct__field label { display: block; font-size: 12px; font-weight: 600; color: #6b7280; margin-bottom: 5px; text-transform: capitalize; }
.ct__field input {
  width: 100%; padding: 9px 12px; border: 1px solid #e0e3eb; border-radius: 8px;
  font-size: 13px; outline: none; transition: border-color .2s, box-shadow .2s; box-sizing: border-box;
}
.ct__field input:focus { border-color: #00274c; border-width: 2px; padding: 8px 11px; }
.ct__select {
  width: 100%; padding: 9px 12px; border: 1px solid #e0e3eb; border-radius: 8px;
  font-size: 13px; outline: none; transition: border-color .2s, box-shadow .2s; box-sizing: border-box;
  background: #fff; color: #374151; cursor: pointer; appearance: menulist;
}
.ct__select:focus { border-color: #00274c; }
.theme--dark .ct__select { background: #131722; border-color: #2a2e39; color: #d1d4dc; }
.ct__hint { font-size: 13px; color: #6b7280; margin-bottom: 14px; }
.ct__delete-name { margin-top: 8px; color: #374151; }

/* ── Dropzone ── */
.ct__dropzone {
  border: 2px dashed #d1d5db; border-radius: 12px; padding: 32px;
  text-align: center; cursor: pointer; transition: all .2s ease;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  color: #9ca3af;
}
.ct__dropzone:hover { border-color: #ffd451; background: #fffbeb; }
.ct__dropzone--active { border-color: #ffd451; background: #fff8e1; }
.ct__dropzone--has-file { border-color: #10b981; background: #ecfdf5; color: #059669; }
.ct__dropzone p { font-size: 13px; font-weight: 500; margin: 0; }

/* ── Transitions ── */
.ct-fade-enter-active { animation: ctFadeIn .2s ease; }
.ct-fade-leave-active { animation: ctFadeIn .15s ease reverse; }
.ct-slide-enter-active { animation: ctSlideUp .25s cubic-bezier(.16,1,.3,1); }
.ct-slide-leave-active { animation: ctSlideUp .15s ease reverse; }

@keyframes ctFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes ctSlideUp { from { opacity: 0; transform: translateY(16px) scale(.97); } to { opacity: 1; transform: translateY(0) scale(1); } }

/* ── Dark Mode ── */
.theme--dark .ct__title { color: #e4e5eb; }
.theme--dark .ct__table-wrap { background: #1e222d; border-color: #2a2e39; }
.theme--dark .ct__table thead { background: #131722; }
.theme--dark .ct__table th { color: #787b86; border-color: #2a2e39; }
.theme--dark .ct__table td { color: #d1d4dc; border-color: #1e222d; }
.theme--dark .ct__table tbody tr:hover { background: #252a36; }
.theme--dark .ct__code { background: #2a2517; color: #ffd451; }
.theme--dark .ct__empty { color: #787b86; }
.theme--dark .ct__btn { background: #1e222d; border-color: #2a2e39; color: #d1d4dc; }
.theme--dark .ct__btn:hover { background: #252a36; }
.theme--dark .ct__btn--primary { background: #ffd451; color: #1a1a2e; border-color: #ffd451; }
.theme--dark .ct__btn--primary:hover { background: #ffcc33; }
.theme--dark .ct__btn--danger { background: #dc2626; color: #fff; border-color: #dc2626; }
.theme--dark .tc-act { border-color: #2a2e39; color: #787b86; }
.theme--dark .tc-act:hover { border-color: #ffd451; color: #ffd451; background: rgba(255,212,81,.08); }
.theme--dark .tc-act--download:hover { border-color: #3b82f6; color: #3b82f6; background: rgba(59,130,246,.08); }
.theme--dark .tc-act--delete:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,.08); }
.theme--dark .ct__modal { background: #1e222d; }
.theme--dark .ct__modal-header { border-color: #2a2e39; }
.theme--dark .ct__modal-header h3 { color: #d1d4dc; }
.theme--dark .ct__modal-footer { border-color: #2a2e39; }
.theme--dark .ct__modal-x { background: #2a2e39; color: #787b86; }
.theme--dark .ct__modal-x:hover { background: #3b1c1c; color: #ef4444; }
.theme--dark .ct__field input { background: #131722; border-color: #2a2e39; color: #d1d4dc; }
.theme--dark .ct__field input:focus { border-color: #ffd451; border-width: 2px; padding: 8px 11px; }
.theme--dark .ct__field label { color: #787b86; }
.theme--dark .ct__dropzone { border-color: #2a2e39; color: #787b86; }
.theme--dark .ct__dropzone:hover { border-color: #ffd451; background: #2a2517; }
.theme--dark .ct__dropzone--active { border-color: #ffd451; background: #2a2517; }
.theme--dark .ct__dropzone--has-file { border-color: #10b981; background: #0d2818; color: #34d399; }
.theme--dark .ct__page-btn { background: #1e222d; border-color: #2a2e39; color: #d1d4dc; }
.theme--dark .ct__page-btn:hover:not(:disabled) { background: #252a36; }
.theme--dark .ct__pagination { color: #787b86; }
.theme--dark .ct__delete-name { color: #d1d4dc; }
.theme--dark .ct__hint { color: #787b86; }
.theme--dark .ct__placeholders { color: #555960; }
.theme--dark .sb-section { background: #1e222d; border-color: #2a2e39; }
.theme--dark .sb-title { color: #d1d4dc; }
.theme--dark .seg { background: #131722; border-color: #2a2e39; box-shadow: none; }
.theme--dark .seg__btn { color: #787b86; }
.theme--dark .seg__btn--active { color: #111827; }
</style>
